import { useEffect, useState } from "react";
import {
  ClientSafeReportViewV1Schema,
  type ClientSafeReportViewV1,
} from "@enrein/shared-contracts/report-views";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";
import { isClientEvidenceNotReadyError } from "./client-evidence-not-ready.js";

export function ReportsPanel({
  api,
  clientLinkId,
  token,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
}) {
  const [report, setReport] = useState<ClientSafeReportViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const view = ClientSafeReportViewV1Schema.parse(await api.getCurrentClientReport(clientLinkId, token));
        if (!cancelled) {
          setReport(view);
          setError(null);
          setUnavailable(false);
        }
      } catch (err) {
        if (!cancelled) {
          setReport(null);
          const message = err instanceof Error ? err.message : String(err);
          if (isClientEvidenceNotReadyError(message)) {
            setError(null);
            setUnavailable(true);
          } else {
            setUnavailable(false);
            setError(toClientApiErrorMessage(message));
          }
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, clientLinkId, token]);

  if (error || unavailable) {
    return (
      <section className="value-card" aria-label="Search readiness">
        <p className="value-card-eyebrow">Readiness report</p>
        <h2 className="value-card-headline">Search readiness</h2>
        <span className="value-card-status value-card-status--pending">Preparing</span>
        {error ? <p className="warning">{error}</p> : null}
        <p>When your agency publishes a client-safe summary, coverage and disclaimers will appear here.</p>
      </section>
    );
  }

  if (!report) {
    return (
      <section className="value-card loading-panel" aria-label="Search readiness">
        <p className="value-card-eyebrow">Readiness report</p>
        <h2 className="value-card-headline">Search readiness</h2>
        <p>Loading your readiness report…</p>
      </section>
    );
  }

  return (
    <section className="value-card" aria-label="Search readiness">
      <p className="value-card-eyebrow">Readiness report</p>
      <h2 className="value-card-headline">Search readiness</h2>
      <span className="value-card-status value-card-status--ready">Summary available</span>
      <p>{report.headline}</p>
      {report.staleBanner ? <p className="warning">{report.staleBanner}</p> : null}
      <p>
        <strong>Coverage:</strong> {report.coverage.coveragePercent}% — {report.coverage.checksPassed} of{" "}
        {report.coverage.checksTotal} checks passed
      </p>
      <details>
        <summary>What we do not promise</summary>
        <ul>
          {report.whatWeDoNotPromise.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
    </section>
  );
}
